import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { priceShow } from 'shared/lib/priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'

import { InsuranceProgram } from '../../../../model/types'

import s from './styles.module.scss'

interface ProgramOptionProps {
  program: InsuranceProgram
  selected: boolean
  onSelect: (program: InsuranceProgram) => void
  onShowDetails: (program: InsuranceProgram) => void
}

export const ProgramOption: FC<ProgramOptionProps> = ({
  program,
  selected,
  onSelect,
  onShowDetails,
}) => {
  const { t } = useTranslation()
  const formattedCoverage = priceShow(program.coverage)
  const coverage = t('insurance.vzr.program.coverage', {
    coverage: `${formattedCoverage} ${program.currency}`,
  })
  const formattedPrice = priceShow(program.price)

  return (
    <div
      className={clsx(s.root, { [s.selected]: selected })}
      onClick={() => onSelect(program)}
    >
      <div className={s.content}>
        <div className={s.mainInfo}>
          <div className={s.titleRow}>
            <p className={clsx(s.title, 'semi-bold-12')}>{program.title}</p>
            {program.isRecommended && (
              <span className={clsx(s.badge, 'medium-12')}>
                {t('insurance.vzr.program.recommended')}
              </span>
            )}
          </div>
          <p className={clsx(s.coverage, 'bold-18')}>{coverage}</p>
          <p className={clsx(s.description, 'medium-12')}>{program.subtitle}</p>
        </div>

        {program.description && (
          <button
            type="button"
            className={clsx(s.details, 'medium-12')}
            onClick={(event) => {
              event.stopPropagation()
              onShowDetails(program)
            }}
          >
            {t('insurance.vzr.program.details')}
          </button>
        )}

        <div className={s.divider} />

        <div className={s.priceRow}>
          <div className={s.priceContent}>
            <p className={clsx(s.price, 'bold-18')}>
              {formattedPrice
                ? t('insurance.vzr.program.price', { price: formattedPrice })
                : t('insurance.vzr.program.price_pending')}
            </p>
            <p className={clsx(s.priceCaption, 'regular-12')}>
              {t('insurance.vzr.program.total_price')}
            </p>
          </div>

          <label
            className={s.radio}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="radio"
              className={s.radioInput}
              id={`insurance-program-${program.id}`}
              name="insurance-program"
              checked={selected}
              aria-label={program.name || coverage}
              onChange={() => onSelect(program)}
            />
            <span className={s.radioControl}>
              {selected && (
                <AppIcon
                  id={AllIcons.CHECK_BOLD}
                  width={16}
                  height={16}
                />
              )}
            </span>
          </label>
        </div>
      </div>
    </div>
  )
}
